import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMemberAuth } from "../../context/MemberAuthContext";
import { memberApi } from "../../lib/api";
import MemberLayout from "../../components/MemberLayout";
import { toast } from "../../hooks/use-toast";
import { Sparkles, ShieldCheck, Save, LogOut } from "lucide-react";

export const Opportunities = () => (
  <MemberLayout>
    <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Members · Opportunities</div>
    <h1 className="hero-title text-[40px] md:text-[56px] leading-none mb-6">Opportunities <span className="font-serif-i text-[#e0684d]">coming soon</span></h1>
    <div className="rounded-lg p-8 md:p-12" style={{ background: '#fbf8ef' }}>
      <div className="w-12 h-12 rounded-md flex items-center justify-center mb-5" style={{ background: '#e0684d', color: 'white' }}><Sparkles size={20} /></div>
      <h2 className="hero-title text-[28px] leading-tight mb-3 max-w-[640px]">Use your professional skills to support Nigerian organisations.</h2>
      <p className="text-[15px] text-[#0d2a20]/80 leading-relaxed mb-6 max-w-[680px]">Soon, you'll be able to browse short, focused projects — typically <strong>2 hours a week for 4 weeks</strong> — where organisations need particular expertise. Because we already have your profile, applying will be lightweight. Once selected by an admin, you'll be notified and matched to a project.</p>
      <div className="grid md:grid-cols-3 gap-4 max-w-[900px]">
        {[
          { t: 'Browse', d: 'See problems, skills required, and time commitment upfront.' },
          { t: 'Apply', d: 'One click with your existing profile — no CV to re-upload.' },
          { t: 'Contribute', d: 'When selected, you\'ll receive an email and the project details.' },
        ].map(x => (
          <div key={x.t} className="rounded-md p-4" style={{ background: '#f4efe4' }}>
            <div className="font-serif-i text-[#e0684d] text-[22px] mb-1">{x.t}</div>
            <div className="text-[13px] text-[#0d2a20]/80">{x.d}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 inline-flex items-center gap-2 px-3 py-2 rounded-md text-[12px]" style={{ background: '#ece5d3', color: '#0d2a20' }}><ShieldCheck size={14} /> We'll email you the moment the first opportunities go live.</div>
    </div>
  </MemberLayout>
);

export const Account = () => {
  const { member, updateAccount, logout } = useMemberAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ display_name: '', email: '', phone: '' });
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [pwdBusy, setPwdBusy] = useState(false);

  useEffect(() => { if (member) setForm({ display_name: member.display_name || '', email: member.email || '', phone: member.phone || '' }); }, [member]);

  if (!member) return null;

  const saveAccount = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await updateAccount(form); toast({ title: 'Account updated' }); }
    catch (err) { toast({ title: 'Could not update', description: err?.response?.data?.detail || 'Try again.' }); }
    finally { setBusy(false); }
  };

  const savePwd = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 8) return toast({ title: 'Password too short' });
    if (pwd.next !== pwd.confirm) return toast({ title: 'Passwords do not match' });
    setPwdBusy(true);
    try { await memberApi.post('/member/change-password', { current_password: pwd.current, new_password: pwd.next }); toast({ title: 'Password changed' }); setPwd({ current: '', next: '', confirm: '' }); }
    catch (err) { toast({ title: 'Could not change', description: err?.response?.data?.detail || 'Try again.' }); }
    finally { setPwdBusy(false); }
  };

  return (
    <MemberLayout>
      <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Members · Account</div>
      <h1 className="hero-title text-[40px] md:text-[56px] leading-none mb-2">Your <span className="font-serif-i text-[#e0684d]">account</span></h1>
      <p className="text-[15px] text-[#0d2a20]/75 mb-6 max-w-[640px]">Private account details. Your email and phone are never shown to other members.</p>

      <form onSubmit={saveAccount} className="rounded-lg p-6 md:p-8 space-y-4 mb-6" style={{ background: '#fbf8ef' }}>
        <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Contact & name</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="text-[12px] text-[#0d2a20]/70">Display name</label><input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
          <div><label className="text-[12px] text-[#0d2a20]/70">Email (private)</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
          <div><label className="text-[12px] text-[#0d2a20]/70">Phone (private)</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
        </div>
        <button disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-coral text-[14px] disabled:opacity-60"><Save size={14} /> {busy ? 'Saving…' : 'Save changes'}</button>
      </form>

      <form onSubmit={savePwd} className="rounded-lg p-6 md:p-8 space-y-4 mb-6" style={{ background: '#fbf8ef' }}>
        <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Change password</div>
        <div className="grid md:grid-cols-3 gap-4">
          <div><label className="text-[12px] text-[#0d2a20]/70">Current password</label><input required type="password" value={pwd.current} onChange={e => setPwd({ ...pwd, current: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
          <div><label className="text-[12px] text-[#0d2a20]/70">New password</label><input required type="password" value={pwd.next} onChange={e => setPwd({ ...pwd, next: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
          <div><label className="text-[12px] text-[#0d2a20]/70">Confirm new password</label><input required type="password" value={pwd.confirm} onChange={e => setPwd({ ...pwd, confirm: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
        </div>
        <button disabled={pwdBusy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-green text-[14px] disabled:opacity-60">{pwdBusy ? 'Updating…' : 'Update password'}</button>
      </form>

      <div className="rounded-lg p-6 md:p-8" style={{ background: '#fbf8ef' }}>
        <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Directory participation</div>
        <p className="text-[14px] text-[#0d2a20]/80 mb-4">You are currently <strong>{member.searchable ? 'listed' : 'opted out'}</strong> in the Member Directory. Manage this from your <a href="/member/profile" className="link-underline text-[#e0684d]">profile page</a>.</p>
        <button onClick={() => { logout(); nav('/'); }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-green text-[14px]"><LogOut size={14} /> Sign out of Vemoye</button>
      </div>
    </MemberLayout>
  );
};
